import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

function fmt(value, currency = "EUR") {
  if (value == null) return "—";
  try {
    return new Intl.NumberFormat("es-ES", { style: "currency", currency, maximumFractionDigits: 2, useGrouping: "always" }).format(value);
  } catch { return `${value} ${currency}`; }
}

export function LinkInvoiceDialog({ albaran, invoices, onClose, onLinked, lang }) {
  const [suggestions, setSuggestions] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Show invoices from the same vendor first
    const sameVendor = invoices.filter((i) =>
      albaran.vendor && (i.vendor || "").toLowerCase().includes((albaran.vendor || "").toLowerCase())
    );
    setSuggestions(sameVendor.length ? sameVendor : invoices.slice(0, 30));
  }, [albaran, invoices]);

  const filtered = suggestions.filter((i) => {
    if (!q) return true;
    const ql = q.toLowerCase();
    return (i.vendor || "").toLowerCase().includes(ql) ||
           (i.invoice_number || "").toLowerCase().includes(ql);
  });

  const link = async (invId) => {
    setLoading(true);
    try {
      await api.post(`/albaranes/${albaran.id}/link`, { invoice_id: invId });
      toast.success(lang === "es" ? "Vinculado" : "Linked");
      onLinked(); onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="albaran-link-dialog">
        <DialogHeader>
          <DialogTitle>
            {lang === "es" ? "Vincular a factura" : "Link to invoice"}
          </DialogTitle>
        </DialogHeader>
        <Input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={lang === "es" ? "Buscar factura…" : "Search invoice…"}
          className="h-9 mb-2"
        />
        <div className="space-y-1 max-h-[50vh] overflow-y-auto">
          {filtered.map((i) => (
            <button
              key={i.id}
              onClick={() => link(i.id)}
              disabled={loading}
              className="w-full text-left px-3 py-2 rounded border border-[var(--border-color)] hover:bg-[var(--surface-hover)] flex items-center justify-between"
              data-testid={`link-invoice-${i.id}`}
            >
              <div>
                <div className="text-sm font-medium">{i.vendor || "?"}</div>
                <div className="text-xs text-[var(--text-muted)]">
                  {i.date || "—"} · {i.invoice_number || ""}
                </div>
              </div>
              <div className="font-mono-num text-sm">{fmt(i.amount, i.currency)}</div>
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="text-sm text-center text-[var(--text-muted)] py-6">
              {lang === "es" ? "No hay facturas que coincidan." : "No matching invoices."}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{lang === "es" ? "Cancelar" : "Cancel"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
